import { ref } from "vue";
import { ElMessage } from "element-plus";
import {
  getBaseUrl,
  sendChatMessage,
} from "../assets/js/impactdriver_front/requests";
import { useChatStreaming } from "./useChatStreaming";
import { useMarkdown } from "./useMarkdown";

const stripActions = (text) =>
  text.replace(/```actions[\s\S]*?(?:```|$)/g, "").trim();

export function useActionModals({ buildRequestArgs = () => ({}) } = {}) {
  const draftEmailVisible = ref(false);
  const draftEmailLoading = ref(false);
  const draftEmailContent = ref("");

  const createTaskVisible = ref(false);
  const taskForm = ref({
    title: "",
    body: "",
    due_date: null,
    priority: "MEDIUM",
  });
  const taskFormCrmId = ref("");
  const taskFormCustomerName = ref("");

  const createDealVisible = ref(false);
  const dealForm = ref({ name: "", amount: "" });
  const dealFormCrmId = ref("");
  const dealFormCustomerName = ref("");

  const saveNoteVisible = ref(false);
  const noteForm = ref({ content: "" });
  const noteFormCrmId = ref("");

  const actionLoading = ref(false);
  const actionSuccessVisible = ref(false);
  const actionSuccessUrl = ref("");

  const extractActions = (content) => {
    if (!content) return [];
    const actions = [];
    const re = /```actions\s*\n([\s\S]*?)```/g;
    let match;
    while ((match = re.exec(content)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        if (Array.isArray(parsed)) actions.push(...parsed);
      } catch (err) {
        console.error("Failed to parse actions JSON:", match[1], err);
      }
    }
    return actions;
  };

  const actionButtonType = (type) => {
    switch (type) {
      case "draft_email":
        return "warning";
      case "create_task":
        return "primary";
      case "create_deal":
        return "success";
      case "save_note":
        return "info";
      default:
        return "default";
    }
  };

  const actionButtonIcon = (type) => {
    switch (type) {
      case "draft_email":
        return "Message";
      case "create_task":
        return "Finished";
      case "create_deal":
        return "Money";
      case "save_note":
        return "Document";
      default:
        return "Document";
    }
  };

  const actionButtonMaterialIcon = (type) => {
    switch (type) {
      case "draft_email":
        return "mail";
      case "create_task":
        return "assignment";
      case "create_deal":
        return "contract";
      case "save_note":
        return "description";
      default:
        return "description";
    }
  };

  const actionButtonLabel = (type) => {
    switch (type) {
      case "draft_email":
        return "Draft Email";
      case "create_task":
        return "Create Task";
      case "create_deal":
        return "Create Deal";
      case "save_note":
        return "Save as Note";
      default:
        return type;
    }
  };

  const actionButtonPrefix = (type) => {
    switch (type) {
      case "draft_email":
        return "Email";
      case "create_task":
        return "Task";
      case "create_deal":
        return "Deal";
      case "save_note":
        return "Note";
      default:
        return "";
    }
  };

  const actionButtonTitle = (action) => {
    if (!action) return "";
    const customerName = (action.customer_name || "").trim();

    // Connectors like "&" don't count as words (e.g., "Renew & Rescue" stays intact)
    const limitToTwoWords = (s) => {
      const t = (s || "").trim().replace(/\s+/g, " ");
      const words = t.split(" ");
      let count = 0;
      let result = [];
      for (const word of words) {
        if (word === "&" || word.toLowerCase() === "and" || word === "-") {
          result.push(word);
        } else {
          count++;
          result.push(word);
          if (count >= 2) break;
        }
      }
      return result.join(" ");
    };

    let title = "";
    switch (action.type) {
      case "create_task":
        title = limitToTwoWords(action.suggested_title);
        break;
      case "create_deal":
        title = limitToTwoWords(action.suggested_name);
        break;
      case "save_note":
        title =
          limitToTwoWords(action.suggested_title) ||
          limitToTwoWords(action.suggested_content);
        break;
      case "draft_email":
        title =
          limitToTwoWords(action.suggested_title) ||
          limitToTwoWords(action.context);
        break;
      default:
        break;
    }
    // Customer name is never truncated
    if (title && customerName) {
      return `${title} - ${customerName}`;
    }
    return title || customerName;
  };

  const openActionModal = (action, conversationHistory = []) => {
    switch (action.type) {
      case "draft_email":
        draftEmailContent.value = "";
        draftEmailLoading.value = true;
        draftEmailVisible.value = true;
        streamDraftEmail(action, conversationHistory);
        break;
      case "create_task":
        taskFormCrmId.value = action.customer_crm_id;
        taskFormCustomerName.value = action.customer_name || "";
        taskForm.value = {
          title: action.suggested_title || "",
          body: action.suggested_body || "",
          due_date: new Date(Date.now() + 86400000),
          priority: "MEDIUM",
        };
        createTaskVisible.value = true;
        break;
      case "create_deal":
        dealFormCrmId.value = action.customer_crm_id;
        dealFormCustomerName.value = action.customer_name || "";
        dealForm.value = {
          name: action.suggested_name || "",
          amount: action.suggested_amount || "",
        };
        createDealVisible.value = true;
        break;
      case "save_note":
        noteFormCrmId.value = action.customer_crm_id;
        noteForm.value = { content: action.suggested_content || "" };
        saveNoteVisible.value = true;
        break;
    }
  };

  const submitCreateTask = async () => {
    actionLoading.value = true;
    try {
      const res = await fetch(`${getBaseUrl()}/api/chat/actions/create-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_crm_id: taskFormCrmId.value,
          title: taskForm.value.title,
          body: taskForm.value.body,
          due_date: taskForm.value.due_date
            ? new Date(taskForm.value.due_date).toISOString()
            : null,
          priority: taskForm.value.priority,
        }),
      });
      const data = await res.json();
      if (data.success) {
        createTaskVisible.value = false;
        actionSuccessUrl.value = data.hubspot_url;
        actionSuccessVisible.value = true;
      } else {
        ElMessage.error(data.error || "Failed to create task");
      }
    } catch {
      ElMessage.error("Failed to create task");
    } finally {
      actionLoading.value = false;
    }
  };

  const streamDraftEmail = async (action, conversationHistory = []) => {
    const history = conversationHistory.map((m) => ({
      role: m.role,
      content: stripActions(m.content),
    }));

    const message = `Draft a professional email for ${action.customer_name} about: ${action.context}. Format it as a ready-to-send email with subject line, greeting, body, and sign-off. Do not include any suggested actions.`;

    try {
      const response = await sendChatMessage(message, {
        ...buildRequestArgs(),
        history,
      });
      const { processStreamingResponse } = useChatStreaming();

      await processStreamingResponse(response, {
        onContent: (chunk) => {
          draftEmailContent.value += chunk;
        },
      });
    } catch (err) {
      ElMessage.error("Failed to generate email draft");
    } finally {
      draftEmailLoading.value = false;
    }
  };

  const submitCreateDeal = async () => {
    actionLoading.value = true;
    try {
      const res = await fetch(`${getBaseUrl()}/api/chat/actions/create-deal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_crm_id: dealFormCrmId.value,
          name: dealForm.value.name,
          amount: dealForm.value.amount || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        createDealVisible.value = false;
        actionSuccessUrl.value = data.hubspot_url;
        actionSuccessVisible.value = true;
      } else {
        ElMessage.error(data.error || "Failed to create deal");
      }
    } catch {
      ElMessage.error("Failed to create deal");
    } finally {
      actionLoading.value = false;
    }
  };

  const submitSaveNote = async () => {
    actionLoading.value = true;
    try {
      const res = await fetch(`${getBaseUrl()}/api/chat/actions/save-note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_crm_id: noteFormCrmId.value,
          content: noteForm.value.content,
        }),
      });
      const data = await res.json();
      if (data.success) {
        saveNoteVisible.value = false;
        actionSuccessUrl.value = data.hubspot_url;
        actionSuccessVisible.value = true;
      } else {
        ElMessage.error(data.error || "Failed to save note");
      }
    } catch {
      ElMessage.error("Failed to save note");
    } finally {
      actionLoading.value = false;
    }
  };

  const copyToClipboard = async (content) => {
    try {
      await useMarkdown().copyMarkdownToClipboard(stripActions(content));
      ElMessage.success("Copied to clipboard");
    } catch {
      ElMessage.error("Failed to copy to clipboard");
    }
  };

  return {
    draftEmailVisible,
    draftEmailLoading,
    draftEmailContent,
    createTaskVisible,
    taskForm,
    taskFormCustomerName,
    createDealVisible,
    dealForm,
    dealFormCustomerName,
    saveNoteVisible,
    noteForm,
    actionLoading,
    actionSuccessVisible,
    actionSuccessUrl,

    extractActions,
    actionButtonType,
    actionButtonIcon,
    actionButtonMaterialIcon,
    actionButtonLabel,
    actionButtonPrefix,
    actionButtonTitle,
    openActionModal,
    submitCreateTask,
    submitCreateDeal,
    submitSaveNote,
    copyToClipboard,
  };
}
